import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'

export const REPORT_PREVIEW_ACTION_ID = 'report.preview'

type ReportPreviewArgs = {
  prompt: string
  selectedTargetCount: number
  boardElementCount: number
  checklistItemCount: number
}

export type ReportPreviewResult = { previewText: string }

const reportPreviewArgsSchema: JSONSchemaType<ReportPreviewArgs> = {
  type: 'object',
  properties: {
    prompt: { type: 'string' },
    selectedTargetCount: { type: 'integer', minimum: 0 },
    boardElementCount: { type: 'integer', minimum: 0 },
    checklistItemCount: { type: 'integer', minimum: 0 },
  },
  required: ['prompt', 'selectedTargetCount', 'boardElementCount', 'checklistItemCount'],
  additionalProperties: false,
}

const parseReportKind = (prompt: string): string => {
  if (/\bweekly\b/i.test(prompt)) return 'weekly'
  if (/\bmonthly\b/i.test(prompt)) return 'monthly'
  if (/\baudit\b/i.test(prompt)) return 'audit'
  if (/\bsummary\b/i.test(prompt)) return 'summary'
  return 'workspace'
}

const reportPreviewAction: ActionDefinition<ReportPreviewArgs, ReportPreviewResult> = {
  id: REPORT_PREVIEW_ACTION_ID,
  label: 'Report preview',
  description: 'Previews a report without generating or saving it.',
  risk: 'READ_ONLY',
  domain: 'report',
  surfaces: ['ai', 'catalog'],
  argsSchema: reportPreviewArgsSchema,
  permission: (context) => context.canPerform('workspace.view_reports'),
  run: async (args, context): Promise<ActionOutcome<ReportPreviewResult>> => {
    const kind = parseReportKind(args.prompt)
    const previewText = [
      `Report kind: ${kind}.`,
      `Signals in scope: ${args.boardElementCount} timeline element${args.boardElementCount === 1 ? '' : 's'}, ${args.checklistItemCount} checklist item${args.checklistItemCount === 1 ? '' : 's'}.`,
      args.selectedTargetCount > 0 ? `Explicit targets: ${args.selectedTargetCount}.` : 'No explicit targets — I would draw from the whole workspace.',
      'Reply "generate" to run the real report through the wizard; this preview does not persist.',
    ].join(' ')
    return { kind: 'COMPLETED', result: { previewText }, message: previewText, correlationId: context.correlationId! }
  },
}

export const registerReportPreviewAction = (): void => {
  if (!getAction(REPORT_PREVIEW_ACTION_ID)) registerAction(reportPreviewAction)
}

registerReportPreviewAction()
